import { getAuth } from 'firebase/auth';

/**
 * Single entry point for talking to the API.
 *
 * Two things every authenticated call needs, and both are easy to forget at a
 * call site: the caller's Firebase ID token, and which database the request is
 * meant for. Putting them here means no page has to remember either.
 *
 * Note that requests no longer carry a refid. The server derives it from the
 * verified token, because the refid used to live in the Firebase displayName,
 * which any signed-in client can rewrite.
 */

const DATA_SOURCE_KEY = 'onlyone.dataSource';

export const LIVE = 'live';
export const DEV = 'dev';

export function getDataSource() {
  try {
    return window.localStorage.getItem(DATA_SOURCE_KEY) === DEV ? DEV : LIVE;
  } catch {
    // Private browsing and similar can make localStorage throw on read.
    return LIVE;
  }
}

export function setDataSource(source) {
  try {
    window.localStorage.setItem(DATA_SOURCE_KEY, source === DEV ? DEV : LIVE);
  } catch {
    /* falls back to live for the rest of the session */
  }
}

/**
 * fetch() with the ID token and the selected data source attached.
 *
 * Resolves to the Response, exactly like fetch, so existing call sites keep
 * working. Use apiJson when a parsed body and an error on failure are wanted.
 */
export async function apiFetch(path, options = {}) {
  const headers = { ...(options.headers || {}) };
  headers['X-Data-Source'] = getDataSource();

  const user = getAuth().currentUser;
  if (user) {
    // getIdToken refreshes when the cached token is close to expiring.
    headers.Authorization = `Bearer ${await user.getIdToken()}`;
  }
  if (options.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  return fetch(path, { ...options, headers });
}

/** apiFetch, parsed, throwing on a non-2xx so callers do not have to check. */
export async function apiJson(path, options = {}) {
  const response = await apiFetch(path, options);
  let body = null;
  try {
    body = await response.json();
  } catch {
    /* an empty or non-JSON body stays null */
  }
  if (!response.ok) {
    const error = new Error((body && body.error) || `Request failed (${response.status})`);
    error.status = response.status;
    error.code = body && body.code;
    throw error;
  }
  return body;
}
